import { useState, type ReactNode } from 'react'
import { ApiContractError, ApiError } from '@/api/client'
import { MAX_PAGE_SIZE } from '@/api/topics/api'
import type { ProducttypeFunnel, TopicQuery } from '@/api/topics/types'
import { StackedShare } from '@/components/charts/StackedShare'
import { deriveFunnelStatus, type FunnelStatus } from '@/features/topics/funnelStatus'
import { TopicRow } from '@/features/topics/TopicRow'
import { useTopics } from '@/features/topics/useTopics'
import { formatNumber } from '@/lib/format'

const funnelStatusLabels: Readonly<Record<FunnelStatus, string>> = {
  Open: 'Offen',
  Partial: 'Teilweise bearbeitet',
  Complete: 'Bearbeitet',
  Ignored: 'Nicht verfolgt',
}

const funnelStatusStyles: Readonly<Record<FunnelStatus, string>> = {
  Open: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-100',
  Partial: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-100',
  Complete: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100',
  Ignored: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100',
}

interface ProducttypeFunnelCardProps {
  readonly funnel: ProducttypeFunnel
  /** Dieselben Filter wie die Hauptrichter-Liste, ohne producttypeKey. */
  readonly topicFilters: Omit<TopicQuery, 'producttypeKey' | 'page' | 'pageSize'>
  readonly pendingTopicId: number | null
  readonly onApprove: (topicId: number) => void
  readonly onIgnore: (topicId: number) => void
  readonly onReset: (topicId: number) => void
  readonly defaultExpanded?: boolean
}

export function ProducttypeFunnelCard({
  funnel,
  topicFilters,
  pendingTopicId,
  onApprove,
  onIgnore,
  onReset,
  defaultExpanded = false,
}: ProducttypeFunnelCardProps): ReactNode {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const status = deriveFunnelStatus(funnel.topicCounts)

  const {
    data: topicPage,
    isLoading,
    error,
  } = useTopics(
    {
      ...topicFilters,
      producttypeKey: funnel.producttypeKey,
      sortBy: 'SearchVolumeMax',
      sortDirection: 'Descending',
      pageSize: MAX_PAGE_SIZE,
    },
    expanded,
  )

  return (
    <article className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => {
          setExpanded((value) => !value)
        }}
        className="w-full p-5 text-left transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500 dark:hover:bg-slate-700/40"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span aria-hidden className="text-slate-400 dark:text-slate-500">
                {expanded ? '▾' : '▸'}
              </span>
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                {funnel.producttypeName}
              </h2>
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${funnelStatusStyles[status]}`}
              >
                {funnelStatusLabels[status]}
              </span>
            </div>
            <p className="mt-1 pl-5 text-xs text-slate-500 dark:text-slate-400">
              {funnel.producttypeKey} · {formatNumber(funnel.topicCounts.total)} Themen
            </p>
          </div>

          <div className="text-right">
            <p className="text-xs text-slate-500 dark:text-slate-400">Bekanntes Potenzial</p>
            <p className="text-xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
              {formatNumber(funnel.potential.total)}
              <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">
                / Monat
              </span>
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-3 border-t border-slate-100 pt-4 text-sm dark:border-slate-700">
          <div>
            <span className="text-slate-500 dark:text-slate-400">Offen</span>
            <p className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">
              {formatNumber(funnel.potential.open)} · {formatNumber(funnel.topicCounts.open)}
            </p>
          </div>
          <div>
            <span className="text-slate-500 dark:text-slate-400">Freigegeben</span>
            <p className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">
              {formatNumber(funnel.potential.approved)} ·{' '}
              {formatNumber(funnel.topicCounts.approved)}
            </p>
          </div>
          <div>
            <span className="text-slate-500 dark:text-slate-400">Ignoriert</span>
            <p className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">
              {formatNumber(funnel.potential.ignored)} · {formatNumber(funnel.topicCounts.ignored)}
            </p>
          </div>
        </div>

        {funnel.topicsWithoutPotential > 0 && (
          <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">
            {formatNumber(funnel.topicsWithoutPotential)}{' '}
            {funnel.topicsWithoutPotential === 1 ? 'Thema hat' : 'Themen haben'} keinen Trafficwert.
          </p>
        )}
      </button>

      <div className="border-t border-slate-100 px-5 py-3 dark:border-slate-700">
        <StackedShare
          segments={[
            {
              label: 'Offenes Potenzial',
              value: funnel.potential.open,
              color: 'var(--viz-series-1)',
            },
            {
              label: 'Freigegeben',
              value: funnel.potential.approved,
              color: 'var(--viz-series-3)',
            },
            {
              label: 'Ignoriert',
              value: funnel.potential.ignored,
              color: 'var(--viz-series-2)',
            },
          ]}
        />
      </div>

      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              Sub-Trichter nach Potenzial
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Traffic ist das stärkste Keyword je Themencluster.
            </p>
          </div>

          {isLoading && (
            <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
              Themen werden geladen…
            </p>
          )}

          {error && (
            <p className="py-6 text-center text-sm text-red-800 dark:text-red-100">
              {error instanceof ApiError || error instanceof ApiContractError
                ? error.message
                : 'Die Themen konnten nicht geladen werden.'}
            </p>
          )}

          {topicPage && (
            <div className="space-y-3">
              {topicPage.items.map((topic) => (
                <TopicRow
                  key={topic.id}
                  topic={topic}
                  showProducttype={false}
                  isPending={pendingTopicId === topic.id}
                  onApprove={onApprove}
                  onIgnore={onIgnore}
                  onReset={onReset}
                />
              ))}
              {topicPage.totalCount > topicPage.items.length && (
                <p className="pt-1 text-xs text-amber-700 dark:text-amber-300">
                  Zeigt {formatNumber(topicPage.items.length)} von{' '}
                  {formatNumber(topicPage.totalCount)} Themen.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  )
}
